<script setup lang="ts">
import { parse, stringify } from 'qs';
import { faMultiply } from '@fortawesome/pro-regular-svg-icons';
import DurationFilter from './filters/DurationFilter.vue';
import ButtonGroupFilter from './filters/ButtonGroupFilter.vue';
import CheckboxFilter from './filters/CheckboxFilter.vue';
import PriceFilter from './filters/PriceFilter.vue';
import RadioFilter from './filters/RadioFilter.vue';
import TimeFilter from './filters/TimeFilter.vue';
import StarFilter from './filters/StarFilter.vue';
import FilterPart from './filters/FilterPart.vue';
import InputFilter from './filters/InputFilter.vue';
import SidebarBox from './SidebarBox.vue';
import {
    type Filter,
    type FilterComponents,
    type PriceFilter as PF,
} from '@itinerary/ItineraryModuleTypes';
import { ReserveType } from '@utils/itinerary';
import { useCurrentSearchForm } from '@composable/useCurrentSearchForm';

const { autoApply } = defineProps<{
    autoApply?: boolean;
}>();
const emit = defineEmits<{
    toggle: [];
}>();

const itineraryStore = useItineraryStore();
const gtm = useGtm();
const router = useRouter();
const route = useRoute();
const isMounted = useMounted();
const filterValues = ref<Record<string, any>>({});
const currentModule = useCurrentItineraryModule();
const filters = computed<Filter[]>(() => {
    if (loading.value) {
        return [];
    }
    return itineraryStore.filters.filter(filter => {
        let is = true;
        if (filter.component === 'PriceFilter') {
            is = !!(filter as PF).min || !!(filter as PF).max;
        }
        return is && (!filter.items || !!filter.items.length);
    });
});
const filtersComponents: Record<FilterComponents, any> = {
    DurationFilter,
    ButtonGroupFilter,
    CheckboxFilter,
    PriceFilter,
    RadioFilter,
    TimeFilter,
    StarFilter,
    InputFilter,
};
const showFilters = computed(() => !itineraryStore.noResults);
const packageSelectStore = usePackageSelectStore();
const loading = computed(() => itineraryStore.filtersLoading);
const showing = computed(() => itineraryStore.packagesLength);
const total = computed(() => itineraryStore.unfilteredCount);
const allFilters = computed(() =>
    loading.value ? [] : itineraryStore.filters
);
const filterPath = computed(() => {
    const filterNames = allFilters.value.map(filter => filter.name);
    const query = parse(route.fullPath.split('#')[0].split('?')[1], {
        allowDots: true,
    });
    return Object.entries(query)
        .filter(([key]) => filterNames.includes(key))
        .reduce(
            (previousValue, [currKey, currValue]) => ({
                ...previousValue,
                [currKey]: currValue,
            }),
            {} as Record<string, any>
        );
});
const filtersClear = computed(() => {
    const filtersKeys = Object.keys(filterPath.value);
    return (
        filtersKeys.length === 0 ||
        (filtersKeys.length === 1 && filtersKeys.includes('sortBy'))
    );
});
const currentSearchForm = useCurrentSearchForm();
const applyFilter = (value: any, name: string) => {
    gtm?.trackEvent({
        event: 'srp-filter-change',
        value: name,
    });
    filterValues.value = {
        ...filterValues.value,
        [name]: value,
    };
    if (autoApply) {
        apply();
    }
};
const clear = () => {
    filters.value.forEach(e => {
        filterValues.value[e.name] = null;
    });
    if (autoApply) {
        apply();
    }
};

const clearableFilters = computed(() =>
    filters.value.filter(e => isValid(filterValues.value[e.name]))
);
const apply = () => {
    const prevCounts = JSON.parse(
        localStorage.getItem('filters') || '{}'
    ) as Record<string, number>;
    const newCounts = Object.entries(filterValues.value).reduce(
        (previousValue, [currKey, currVal]) => {
            if (currKey.startsWith('_')) {
                return previousValue;
            }
            if (!currVal || (Array.isArray(currVal) && !currVal?.length)) {
                return {
                    ...previousValue,
                    [currKey]: prevCounts[currKey] || 0,
                };
            } else {
                return {
                    ...previousValue,
                    [currKey]: (prevCounts[currKey] || 0) + 1,
                };
            }
        },
        {} as Record<string, number>
    );
    localStorage.setItem('filters', JSON.stringify(newCounts));
    currentModule.value.setFilterValues(filterValues.value);
    const prevParams = parse(route.fullPath.split('#')[0].split('?')[1], {
        allowDots: true,
    });
    const filterValuesNormalized = stringify(
        { ...prevParams, ...filterValues.value },
        {
            addQueryPrefix: true,
            arrayFormat: 'brackets',
            allowDots: true,
            skipNulls: true,
        }
    );
    const newPath = `${route.path}${filterValuesNormalized}`;
    if (route.fullPath.split('#')[0] !== newPath || route.hash) {
        const reserveType = currentSearchForm.value.reserveInfo.type;

        const isInternational =
            reserveType === ReserveType.Flight &&
            currentSearchForm.value.isForeign;

        if (isInternational) {
            router.replace(newPath);
        } else {
            window.history.replaceState(null, '', newPath);
        }
    }
    window?.scrollTo({
        top: 0,
        behavior: 'smooth',
    });
};
const refresh = () => {
    if (!itineraryStore.filterValues) {
        return;
    }
    filters.value.forEach(e => {
        filterValues.value[e.name] =
            itineraryStore.filterValues![e.name] || null;
    });
    if (autoApply) {
        apply();
    }
};

const clearFilterPart = (name: string) => {
    if (filterValues.value?.[name]) {
        filterValues.value[name] = null;
        apply();
    }
};

function isValid(value: any): boolean {
    if (!value) {
        return false;
    } // filters out false, 0, '', null, undefined, NaN
    if (typeof value === 'object') {
        if (Array.isArray(value)) {
            return value.length > 0; // non-empty array only
        } else {
            return Object.values(value).some(filterVal => isValid(filterVal)); // non-empty object only
        }
    }
    return true; // all other truthy values
}

watch(
    [loading, () => allFilters.value.length],
    ([value, length]) => {
        if (value || !length) {
            return;
        }
        const [, prevParams] = route.fullPath.split('?');
        const prevParamsNormalized = parse(prevParams, {
            allowDots: true,
        });
        const newFilters = filters.value.reduce(
            (prev, curr) => ({
                ...prev,
                [curr.name]: prevParamsNormalized[curr.name] || null,
            }),
            {}
        );
        currentModule.value.setFilterValues(newFilters);
        filterValues.value = { ...newFilters };
    },
    { immediate: true }
);

watch(
    () => packageSelectStore.selectedPackages.length,
    () => {
        filterValues.value = itineraryStore.filterValues ?? filterValues.value;
    }
);

watch(
    filtersClear,
    (newV, oldV) => {
        if (oldV !== newV && newV) {
            currentModule.value.setFilterValues({});
        }
    },
    { immediate: true }
);
onMounted(() => {
    filterValues.value = itineraryStore.filterValues || {};
});

defineExpose({
    refresh,
    apply,
});
</script>

<template>
    <SidebarBox v-if="showFilters" title="فیلترها" class="root-container">
        <template v-if="!loading && isMounted">
            <div class="selected-filters">
                <mr-button
                    v-for="filter in clearableFilters"
                    :key="filter.title"
                    :left-icon="faMultiply"
                    class="selected-filter"
                    variant="transparent"
                    size="small"
                    @click="clearFilterPart(filter.name)"
                >
                    {{ filter.title }}
                </mr-button>
            </div>
            <div class="pre-header">
                <div class="content">
                    <p>
                        {{ `نمایش ${showing} از ${total} نتیجه` }}
                    </p>
                    <mr-button
                        v-show="!filtersClear"
                        variant="transparent"
                        size="small"
                        @click="clear"
                    >
                        حذف فیلترها
                    </mr-button>
                </div>
            </div>
            <template v-for="(filter, index) in filters" :key="filter.name">
                <FilterPart
                    :title="filter.title"
                    initial-state="closed"
                    :selected="isValid(filterValues[filter.name])"
                    @update:model-value="emit('toggle')"
                >
                    <component
                        :is="filtersComponents[filter.component]"
                        :model-value="filterValues[filter.name]"
                        v-bind="filter"
                        @update:model-value="
                            (value: any) => applyFilter(value, filter.name)
                        "
                    />
                </FilterPart>
                <div v-if="index !== filters.length - 1" class="divider" />
            </template>
        </template>
        <mr-bullet-list-loader
            v-else
            class="loader"
            :count="10"
            :item-height="24"
            :item-max-width="120"
            :item-min-width="60"
        />
    </SidebarBox>
</template>

<style scoped lang="scss">
.root-container {
    .selected-filters {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        padding-inline: 4px;
        margin-block: 8px;
        :deep(.mr-button) {
            background-color: usePalette($p-blue, $p-200) !important;
            border-radius: 9999px;
        }
    }
    .clear {
        margin-right: auto;
    }
    .flight-selection {
        display: flex;
        align-items: center;
        gap: 8px;
        padding-inline: 8px;

        :deep(.mr-button) {
            border-radius: 400px !important;
            &.selected {
                background-color: usePalette($p-blue, $p-100);
            }
        }
    }
    .pre-header {
        display: flex;
        flex-direction: column;
        padding: 16px 16px 0;
        .content {
            align-items: center;
            display: flex;
            justify-content: space-between;
            padding-bottom: 8px;
            p {
                @include tg-m($w5, usePalette($p-cool-gray, $p-500));
            }
        }
    }
    .divider {
        width: 100%;
        height: 2px;
        background-color: usePalette($p-gray, $p-100);
    }
    p {
        margin: 0;
        font-size: 12px;
        @include font-weight();
    }
}

.loader {
    margin-right: 12px;
}

:deep(.vue-slider-dot-tooltip-text) {
    font-size: 10px;
}
</style>
